// src/hooks/useUnsavedChanges.ts
import { useState, useEffect, useRef } from 'react';
import { useModelStore } from '@stores/modelStore';
import { useGroupStore } from '@stores/groupStore';

/**
 * Hook to track whether the current diagram has unsaved changes
 * Compares current model state with last saved state
 */
export const useUnsavedChanges = () => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Track the last saved model state to detect actual changes
  const lastSavedModelRef = useRef<string>('');

  // Watch all model properties that should trigger unsaved state
  const items = useModelStore((state) => state.items);
  const views = useModelStore((state) => state.views);
  const title = useModelStore((state) => state.title);
  const description = useModelStore((state) => state.description);
  const colors = useModelStore((state) => state.colors);
  const icons = useModelStore((state) => state.icons);
  const groups = useGroupStore((state) => state.groups);
  const groupInstances = useGroupStore((state) => state.groupInstances);

  // Track changes to model - compare with last saved state
  useEffect(() => {
    // Create a comprehensive snapshot of the current state
    const currentModelString = JSON.stringify({
      items,
      views,
      title,
      description,
      colors,
      icons,
      groups,
      groupInstances
    });

    // On first render, just store the initial state
    if (!lastSavedModelRef.current) {
      lastSavedModelRef.current = currentModelString;
      return;
    }

    // Only mark as unsaved if the model actually changed from last save
    if (currentModelString !== lastSavedModelRef.current) {
      setHasUnsavedChanges(true);
    }
  }, [items, views, title, description, colors, icons, groups, groupInstances]);

  /**
   * Mark current state as saved
   * Should be called after successful save operations
   */
  const markAsSaved = () => {
    const currentModelString = JSON.stringify({
      items,
      views,
      title,
      description,
      colors,
      icons,
      groups,
      groupInstances
    });
    lastSavedModelRef.current = currentModelString;
    setHasUnsavedChanges(false);
  };

  /**
   * Reset tracking (e.g., when loading a new diagram)
   */
  const reset = () => {
    const currentModelString = JSON.stringify({
      items,
      views,
      title,
      description,
      colors,
      icons,
      groups,
      groupInstances
    });
    lastSavedModelRef.current = currentModelString;
    setHasUnsavedChanges(false);
  };

  return {
    hasUnsavedChanges,
    markAsSaved,
    reset
  };
};
